import { useState, useEffect } from 'react';
import { X, Loader2, Plus, Trash2 } from 'lucide-react';
import { NumberField } from '@/cowork/shared/NumberField';
import type { Agent, Skill } from '../types/cowork';
import { coworkApi } from '../api/coworkApi';

interface AgentEditModalProps {
  agent: Agent;
  onClose: () => void;
  onSaved: () => void;
}

export function AgentEditModal({ agent, onClose, onSaved }: AgentEditModalProps) {
  // ─── Form state ─────────────────────────────
  const [name, setName] = useState(agent.name);
  const [title, setTitle] = useState(agent.title);
  const [tagline, setTagline] = useState(agent.tagline);
  const [avatarEmoji, setAvatarEmoji] = useState(agent.avatarEmoji);
  const [avatarUrl, setAvatarUrl] = useState(agent.avatarUrl ?? '');
  const [accentColor, setAccentColor] = useState(agent.accentColor);
  const [gradientFrom, setGradientFrom] = useState(agent.gradientFrom);
  const [gradientTo, setGradientTo] = useState(agent.gradientTo);
  const [personalityTraits, setPersonalityTraits] = useState<string[]>(agent.personalityTraits);
  const [sortOrder, setSortOrder] = useState(agent.sortOrder);
  const [isActive, setIsActive] = useState(agent.isActive);
  const [newTrait, setNewTrait] = useState('');

  // ─── Skills ─────────────────────────────────
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [loadingSkills, setLoadingSkills] = useState(true);

  // ─── Heartbeats ────────────────────────────
  const [heartbeats, setHeartbeats] = useState<Array<{
    id: string; agentId: string; cron: string; prompt: string;
    enabled: boolean; scheduled: boolean; lastRunAt: string | null;
  }>>([]);
  const [loadingHeartbeats, setLoadingHeartbeats] = useState(true);

  // ─── Save state ─────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  useEffect(() => {
    coworkApi.agents.allSkills(agent.id)
      .then(setAllSkills)
      .catch(() => {})
      .finally(() => setLoadingSkills(false));

    coworkApi.heartbeats.list()
      .then(hbs => setHeartbeats(hbs.filter(h => h.agentId === agent.id)))
      .catch(() => {})
      .finally(() => setLoadingHeartbeats(false));
  }, [agent.id]);

  const toggleHeartbeat = async (hbId: string, enabled: boolean) => {
    try {
      await coworkApi.heartbeats.toggle(hbId, enabled);
      setHeartbeats(prev => prev.map(h => h.id === hbId ? { ...h, enabled } : h));
    } catch { /* silent */ }
  };

  function formatCron(cron: string): string {
    if (cron.startsWith('every ')) return cron.replace('every ', '');
    return cron;
  }

  function timeAgo(iso: string | null): string {
    if (!iso) return 'never';
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  function heartbeatLabel(hb: { id: string; prompt: string }): string {
    // Use first line of prompt or clean up the ID
    const firstLine = hb.prompt.split('\n')[0].slice(0, 60);
    if (firstLine.length > 10) return firstLine;
    return hb.id.replace(/-[a-f0-9]{8}$/, '').replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase());
  }

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('idle');
    try {
      await coworkApi.agents.update(agent.id, {
        name, title, tagline, avatarEmoji,
        avatarUrl: avatarUrl || undefined,
        accentColor, gradientFrom, gradientTo,
        personalityTraits, sortOrder, isActive,
      });
      setSaveStatus('saved');
      onSaved();
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const toggleSkill = async (skill: Skill) => {
    const newActive = !skill.isActive;
    try {
      await coworkApi.agents.updateSkill(agent.id, skill.id, { isActive: newActive });
      setAllSkills(prev => prev.map(s => s.id === skill.id ? { ...s, isActive: newActive } : s));
    } catch { /* ignore */ }
  };

  const addTrait = () => {
    const t = newTrait.trim();
    if (t && !personalityTraits.includes(t)) {
      setPersonalityTraits(prev => [...prev, t]);
      setNewTrait('');
    }
  };

  const removeTrait = (trait: string) => {
    setPersonalityTraits(prev => prev.filter(t => t !== trait));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/30" onClick={onClose}>
      <div
        className="w-[520px] bg-white shadow-2xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
              style={{
                background: `linear-gradient(135deg, ${accentColor}20, ${accentColor}10)`,
                border: `1.5px solid ${accentColor}30`,
              }}
            >
              {avatarEmoji}
            </div>
            <div>
              <h2 className="text-[15px] text-[#101828]" style={{ fontWeight: 700 }}>Edit Agent</h2>
              <p className="text-[11px] text-[#98a2b3]">{agent.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#98a2b3] hover:text-[#344054] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* ── Identity ── */}
          <section>
            <h3 className="text-[11px] text-[#98a2b3] uppercase tracking-wider mb-3" style={{ fontWeight: 600 }}>Identity</h3>
            <div className="space-y-3">
              <Field label="Name" value={name} onChange={setName} />
              <Field label="Title" value={title} onChange={setTitle} />
              <Field label="Tagline" value={tagline} onChange={setTagline} multiline />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Avatar Emoji" value={avatarEmoji} onChange={setAvatarEmoji} />
                <Field label="Avatar URL" value={avatarUrl} onChange={setAvatarUrl} placeholder="Optional image URL" />
              </div>
            </div>
          </section>

          {/* ── Appearance ── */}
          <section>
            <h3 className="text-[11px] text-[#98a2b3] uppercase tracking-wider mb-3" style={{ fontWeight: 600 }}>Appearance</h3>
            <div className="grid grid-cols-3 gap-3">
              <ColorField label="Accent" value={accentColor} onChange={setAccentColor} />
              <ColorField label="Gradient From" value={gradientFrom} onChange={setGradientFrom} />
              <ColorField label="Gradient To" value={gradientTo} onChange={setGradientTo} />
            </div>
            {/* Live preview */}
            <div className="mt-3 p-3 rounded-xl border border-gray-100" style={{
              background: `linear-gradient(135deg, ${gradientFrom}15, ${gradientTo}15)`,
            }}>
              <div className="flex items-center gap-2">
                <span className="text-lg">{avatarEmoji}</span>
                <div>
                  <div className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{name}</div>
                  <div className="text-[11px]" style={{ color: accentColor }}>{title}</div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Personality ── */}
          <section>
            <h3 className="text-[11px] text-[#98a2b3] uppercase tracking-wider mb-3" style={{ fontWeight: 600 }}>Personality</h3>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {personalityTraits.map(trait => (
                <span
                  key={trait}
                  className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full"
                  style={{ background: `${accentColor}12`, color: accentColor, fontWeight: 600 }}
                >
                  {trait}
                  <button onClick={() => removeTrait(trait)} className="hover:opacity-60">
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={newTrait}
                onChange={e => setNewTrait(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTrait(); } }}
                placeholder="Add trait…"
                className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 text-[12px] text-[#101828] placeholder:text-[#c4c9d4] focus:outline-none focus:border-[#be43ff]"
              />
              <button
                onClick={addTrait}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] text-[#667085] hover:bg-gray-50 border border-gray-200"
                style={{ fontWeight: 500 }}
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>
          </section>

          {/* ── Settings ── */}
          <section>
            <h3 className="text-[11px] text-[#98a2b3] uppercase tracking-wider mb-3" style={{ fontWeight: 600 }}>Settings</h3>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-[11px] text-[#667085] mb-1" style={{ fontWeight: 500 }}>Sort Order</label>
                <NumberField
                  min={-999}
                  max={9999}
                  value={sortOrder}
                  onChange={n => setSortOrder(n ?? 0)}
                  boxClassName="relative flex w-20 items-center"
                  className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-[12px] text-[#101828] focus:outline-none focus:border-[#be43ff]"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[11px] text-[#667085]" style={{ fontWeight: 500 }}>Active</label>
                <button
                  onClick={() => setIsActive(!isActive)}
                  className={`w-9 h-5 rounded-full transition-colors ${isActive ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${isActive ? 'translate-x-4.5 ml-4' : 'translate-x-0.5 ml-0.5'}`} />
                </button>
              </div>
            </div>
          </section>

          {/* ── Recurring Tasks (Heartbeats) ── */}
          <section>
            <h3 className="text-[11px] text-[#98a2b3] uppercase tracking-wider mb-3" style={{ fontWeight: 600 }}>
              Recurring Tasks ({heartbeats.length})
            </h3>
            {loadingHeartbeats ? (
              <div className="flex items-center gap-2 py-4 text-[#98a2b3]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="text-[12px]">Loading...</span>
              </div>
            ) : heartbeats.length === 0 ? (
              <p className="text-[12px] text-[#c4c9d4] py-3">No recurring tasks configured for this agent.</p>
            ) : (
              <div className="space-y-1.5">
                {heartbeats.map(hb => (
                  <div
                    key={hb.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${
                      hb.enabled ? 'border-gray-100 bg-white' : 'border-gray-50 bg-gray-50/50 opacity-60'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-[#101828] truncate" style={{ fontWeight: 600 }}>
                        {heartbeatLabel(hb)}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-[#98a2b3]">
                        <span className="px-1.5 py-0.5 rounded bg-gray-50" style={{ fontWeight: 500 }}>
                          {formatCron(hb.cron)}
                        </span>
                        <span>Last: {timeAgo(hb.lastRunAt)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleHeartbeat(hb.id, !hb.enabled)}
                      className={`w-9 h-5 rounded-full transition-colors shrink-0 ${hb.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${hb.enabled ? 'translate-x-4.5 ml-4' : 'translate-x-0.5 ml-0.5'}`} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── Skills ── */}
          <section>
            <h3 className="text-[11px] text-[#98a2b3] uppercase tracking-wider mb-3" style={{ fontWeight: 600 }}>
              Skills ({allSkills.length})
            </h3>
            {loadingSkills ? (
              <div className="flex items-center gap-2 py-4 text-[#98a2b3]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="text-[12px]">Loading skills…</span>
              </div>
            ) : allSkills.length === 0 ? (
              <p className="text-[12px] text-[#c4c9d4] py-3">No skills configured for this agent.</p>
            ) : (
              <div className="space-y-1.5">
                {allSkills.map(skill => (
                  <div
                    key={skill.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${
                      skill.isActive ? 'border-gray-100 bg-white' : 'border-gray-50 bg-gray-50/50 opacity-60'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-[#101828] truncate" style={{ fontWeight: 600 }}>
                        {skill.name}
                      </div>
                      <div className="text-[10px] text-[#98a2b3] truncate">{skill.description}</div>
                    </div>
                    <span className="text-[10px] text-[#98a2b3] px-1.5 py-0.5 rounded bg-gray-50" style={{ fontWeight: 500 }}>
                      {skill.category}
                    </span>
                    <button
                      onClick={() => toggleSkill(skill)}
                      className={`w-9 h-5 rounded-full transition-colors shrink-0 ${skill.isActive ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${skill.isActive ? 'translate-x-4.5 ml-4' : 'translate-x-0.5 ml-0.5'}`} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Footer */}
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <div className="text-[11px]" style={{ fontWeight: 500 }}>
            {saveStatus === 'saved' && <span className="text-green-600">Saved!</span>}
            {saveStatus === 'error' && <span className="text-red-500">Save failed</span>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-[12px] text-[#667085] hover:bg-gray-50 transition-colors"
              style={{ fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-xl text-[12px] text-white bg-[#be43ff] hover:bg-[#a035d9] transition-colors disabled:opacity-50"
              style={{ fontWeight: 600 }}
            >
              {saving ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Saving…
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Reusable field components ──────────────────────────────────

function Field({
  label, value, onChange, placeholder, multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  const cls = "w-full px-3 py-1.5 rounded-lg border border-gray-200 text-[12px] text-[#101828] placeholder:text-[#c4c9d4] focus:outline-none focus:border-[#be43ff] transition-colors";
  return (
    <div>
      <label className="block text-[11px] text-[#667085] mb-1" style={{ fontWeight: 500 }}>{label}</label>
      {multiline ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={2} className={`${cls} resize-none`} />
      ) : (
        <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={cls} />
      )}
    </div>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-[11px] text-[#667085] mb-1" style={{ fontWeight: 500 }}>{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-8 h-8 rounded-lg border border-gray-200 cursor-pointer p-0.5"
        />
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          className="flex-1 px-2 py-1.5 rounded-lg border border-gray-200 text-[11px] text-[#101828] font-mono focus:outline-none focus:border-[#be43ff]"
        />
      </div>
    </div>
  );
}
